import React, { useEffect, useEffectEvent, useRef } from "react";
import { EMPTY_IMAGING, EMPTY_LAB, EMPTY_MEDICATION, EMPTY_RESULT, LAB_FIELDS, LAB_FIELD_UNITS, LAB_HIDDEN_FIELDS, LAB_HIDDEN_UNIT_FIELDS } from "./constants";
import { HiddenSourceFields, Modal } from "./primitives";
import type {
  DiagnosisRow,
  FaceSheetPayload,
  FaceSheetPaths,
  FaceSheetSectionKey,
  ResultRow,
} from "./types";
import type { LabField } from "./constants";
import type { LabRow } from "./types";
import { allergyMedicationResponse, isAllergyMedication, rowsForForm, sortLabRowsOldestToNewest } from "./utils";

interface FaceSheetModalFormProps {
  openSection: FaceSheetSectionKey | null;
  payload: FaceSheetPayload;
  paths: FaceSheetPaths;
  authenticityToken: string;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onSubmit: () => void;
}

function formSnapshot(form: HTMLFormElement): string {
  const entries: Array<[string, string]> = [];
  const formData = new FormData(form);

  formData.forEach((value, name) => {
    if (name === "authenticity_token" || name === "_method") return;

    entries.push([ name, typeof value === "string" ? value : value.name ]);
  });

  return JSON.stringify(
    entries
  );
}

function SectionFormActions({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" className="app-button-secondary" onClick={onClose}>
        Cancel
      </button>
      <button type="submit" className="app-button-primary">
        Save
      </button>
    </div>
  );
}

function labFieldLabel(field: LabField, label: string): string {
  const unit = LAB_FIELD_UNITS[field];
  return unit ? `${label} (${unit})` : label;
}

function labReferenceRange(lab: LabRow, field: LabField): string {
  if (field === "calcium") return lab.reference_range || "";
  if (field === "pth") return lab.pth_reference_range || "";

  return "";
}

function LabInputCell({
  field,
  lab,
  index,
  fieldIndex,
}: {
  field: LabField;
  lab: LabRow;
  index: number;
  fieldIndex: number;
}) {
  return (
    <td className="px-2 py-2">
      {fieldIndex === 0 ? (
        <>
          <HiddenSourceFields prefix={`face_sheet[labs][${index}]`} source={lab.source} />
          {LAB_HIDDEN_FIELDS.map((hiddenField) => (
            <React.Fragment key={hiddenField}>
              <HiddenSourceFields
                prefix={`face_sheet[labs][${index}][field_sources][${hiddenField}]`}
                source={lab.field_sources?.[hiddenField]}
                nestUnderSource={false}
              />
              <input type="hidden" name={`face_sheet[labs][${index}][${hiddenField}]`} defaultValue={lab[hiddenField] || ""} />
            </React.Fragment>
          ))}
          {LAB_HIDDEN_UNIT_FIELDS.map((hiddenField) => (
            <input key={hiddenField} type="hidden" name={`face_sheet[labs][${index}][${hiddenField}]`} defaultValue={lab[hiddenField] || ""} />
          ))}
        </>
      ) : null}
      <HiddenSourceFields prefix={`face_sheet[labs][${index}][field_sources][${field}]`} source={lab.field_sources?.[field]} nestUnderSource={false} />
      <input
        type="text"
        name={`face_sheet[labs][${index}][${field}]`}
        defaultValue={lab[field] || ""}
        className="app-input mt-0 w-full min-w-24"
        title={labReferenceRange(lab, field) ? `Reference range: ${labReferenceRange(lab, field)}` : undefined}
      />
    </td>
  );
}

function diagnosisSourceValue(diagnosis: DiagnosisRow, field: "type" | "label"): string {
  return diagnosis.source?.[field] || "";
}

export default function FaceSheetModalForm({
  openSection,
  payload,
  paths,
  authenticityToken,
  onClose,
  onDirtyChange,
  onSubmit,
}: FaceSheetModalFormProps) {
  const formRef = useRef<HTMLFormElement | null>(null);
  const initialSnapshotRef = useRef("");
  const additionalResults = payload.additional_results || {};
  const emitDirtyChange = useEffectEvent(onDirtyChange);
  const handleSaved = useEffectEvent(onSubmit);

  useEffect(() => {
    if (!openSection) {
      initialSnapshotRef.current = "";
      emitDirtyChange(false);
      return;
    }

    if (!formRef.current) return;

    initialSnapshotRef.current = formSnapshot(formRef.current);
    emitDirtyChange(false);
  }, [emitDirtyChange, openSection]);

  const handleFormChange = () => {
    if (!formRef.current) return;

    emitDirtyChange(formSnapshot(formRef.current) !== initialSnapshotRef.current);
  };

  const handleSubmit = () => {
    emitDirtyChange(false);
    handleSaved();
  };

  const formProps = {
    action: paths.update,
    className: "space-y-4",
    method: "post" as const,
    onChange: handleFormChange,
    onInput: handleFormChange,
    onSubmit: handleSubmit,
    ref: formRef,
  };

  if (!openSection) return null;

  let title = "";
  let content: React.ReactNode = null;

  if (openSection === "demographics") {
    title = "Patient and consult context";
    content = (
      <form {...formProps}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="_method" value="patch" />
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200" htmlFor="face-sheet-consult-date">
              Consult date
            </label>
            <input
              id="face-sheet-consult-date"
              type="date"
              name="face_sheet[care_context][consult_date]"
              defaultValue={payload.care_context?.consult_date || ""}
              className="app-input mt-0.5"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200" htmlFor="face-sheet-cpt">
              CPT
            </label>
            <input
              id="face-sheet-cpt"
              type="text"
              name="face_sheet[care_context][cpt_code]"
              defaultValue={payload.care_context?.cpt_code || ""}
              className="app-input mt-0.5"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200" htmlFor="face-sheet-endo">
              Endocrinologist
            </label>
            <input
              id="face-sheet-endo"
              type="text"
              name="face_sheet[care_context][endocrinologist_name]"
              defaultValue={payload.care_context?.endocrinologist_name || ""}
              className="app-input mt-0.5"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200" htmlFor="face-sheet-text-ok">
              Text consent
            </label>
            <select
              id="face-sheet-text-ok"
              name="face_sheet[care_context][text_ok]"
              defaultValue={payload.care_context?.text_ok || ""}
              className="app-select mt-0.5"
            >
              <option value="">Unknown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
        <SectionFormActions onClose={onClose} />
      </form>
    );
  }

  if (openSection === "medications") {
    title = "Current medications and supplements";
    const indexedMedications = (payload.medications || []).map((medication, index) => ({ medication, index }));
    const allergyRows = indexedMedications.filter(({ medication }) => isAllergyMedication(medication));
    const medicationRows = indexedMedications.filter(({ medication }) => !isAllergyMedication(medication));
    const blankMedicationRows = rowsForForm([], EMPTY_MEDICATION, 3).map((medication, offset) => ({
      medication,
      index: indexedMedications.length + offset,
    }));

    content = (
      <form {...formProps}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="_method" value="patch" />
        {allergyRows.length > 0 ? (
          <section className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 dark:border-amber-900/70 dark:bg-amber-950/20">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300">Allergies</p>
              <h4 className="mt-0.5 text-sm font-semibold text-neutral-950 dark:text-white">Allergies response</h4>
            </div>
            <div className="mt-2.5 space-y-2.5">
              {allergyRows.map(({ medication, index }) => (
                <div key={`allergy-medication-${index}`}>
                  <HiddenSourceFields prefix={`face_sheet[medications][${index}]`} source={medication.source} />
                  <input
                    type="text"
                    name={`face_sheet[medications][${index}][name]`}
                    defaultValue={allergyMedicationResponse(medication)}
                    placeholder="Allergies response"
                    className="app-input mt-0"
                  />
                  <input type="hidden" name={`face_sheet[medications][${index}][dose]`} defaultValue={medication.dose || ""} />
                  <input type="hidden" name={`face_sheet[medications][${index}][frequency]`} defaultValue={medication.frequency || ""} />
                  <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                    This comes from the allergy intake question, not the medication list.
                  </p>
                </div>
              ))}
            </div>
          </section>
        ) : null}
        <div className="space-y-2.5">
          {([ ...medicationRows, ...blankMedicationRows ]).map(({ medication, index }) => (
            <div key={`medication-${index}`} className="grid gap-2.5 md:grid-cols-[1.2fr_0.8fr_0.9fr]">
              <HiddenSourceFields prefix={`face_sheet[medications][${index}]`} source={medication.source} />
              <input
                type="text"
                name={`face_sheet[medications][${index}][name]`}
                defaultValue={medication.name || ""}
                placeholder="Medication"
                className="app-input mt-0"
              />
              <input
                type="text"
                name={`face_sheet[medications][${index}][dose]`}
                defaultValue={medication.dose || ""}
                placeholder="Dose"
                className="app-input mt-0"
              />
              <input
                type="text"
                name={`face_sheet[medications][${index}][frequency]`}
                defaultValue={medication.frequency || ""}
                placeholder="Frequency"
                className="app-input mt-0"
              />
            </div>
          ))}
        </div>
        <SectionFormActions onClose={onClose} />
      </form>
    );
  }

  if (openSection === "labs") {
    title = "Lab trend";
    const labs = rowsForForm(sortLabRowsOldestToNewest(payload.labs), EMPTY_LAB, 4);

    content = (
      <form {...formProps}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="_method" value="patch" />
        <div className="overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
              <tr>
                {LAB_FIELDS.map(([field, label]) => (
                  <th key={field} className="px-2 py-2 text-left text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                    {labFieldLabel(field, label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {labs.map((lab, index) => (
                <tr key={`lab-${index}`}>
                  {LAB_FIELDS.map(([field], fieldIndex) => (
                    <LabInputCell
                      key={`${field}-${index}`}
                      field={field}
                      lab={lab}
                      index={index}
                      fieldIndex={fieldIndex}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <SectionFormActions onClose={onClose} />
      </form>
    );
  }

  if (openSection === "additional_results") {
    title = "TSH and 24-hour urine";
    const tshRows = rowsForForm(additionalResults.tsh, EMPTY_RESULT, 2);
    const urineRows = rowsForForm(additionalResults.urine_24h, EMPTY_RESULT, 2);

        const renderResultRows = (name: "tsh" | "urine_24h", rows: ResultRow[]) =>
      rows.map((row, index) => (
        <div key={`${name}-${index}`} className="grid gap-3 md:grid-cols-3">
          <HiddenSourceFields prefix={`face_sheet[additional_results][${name}][${index}]`} source={row.source} />
          <input
            type="hidden"
            name={`face_sheet[additional_results][${name}][${index}][unit]`}
            defaultValue={row.unit || ""}
          />
          <input
            type="text"
            name={`face_sheet[additional_results][${name}][${index}][date]`}
            defaultValue={row.date || ""}
            placeholder="Date"
            className="app-input mt-0"
          />
          <input
            type="text"
            name={`face_sheet[additional_results][${name}][${index}][value]`}
            defaultValue={row.value || ""}
            placeholder="Value"
            className="app-input mt-0"
          />
          <input
            type="text"
            name={`face_sheet[additional_results][${name}][${index}][reference_range]`}
            defaultValue={row.reference_range || ""}
            placeholder="Reference"
            className="app-input mt-0"
          />
        </div>
      ));

    content = (
      <form {...formProps}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="_method" value="patch" />
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2.5">
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">TSH (uIU/mL)</p>
            {renderResultRows("tsh", tshRows)}
          </div>
          <div className="space-y-2.5">
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">24-hour urine (mg/24 hr)</p>
            {renderResultRows("urine_24h", urineRows)}
          </div>
        </div>
        <SectionFormActions onClose={onClose} />
      </form>
    );
  }

  if (openSection === "imaging") {
    title = "Imaging studies";
    const imagingRows = rowsForForm(payload.imaging, EMPTY_IMAGING, 3);

    content = (
      <form {...formProps}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="_method" value="patch" />
        {imagingRows.map((row, index) => (
          <div key={`imaging-${index}`} className="grid gap-2.5 md:grid-cols-[0.8fr_1fr_1.5fr]">
            <HiddenSourceFields prefix={`face_sheet[imaging][${index}]`} source={row.source} />
            <input
              type="text"
              name={`face_sheet[imaging][${index}][date]`}
              defaultValue={row.date || ""}
              placeholder="Date"
              className="app-input mt-0"
            />
            <input
              type="text"
              name={`face_sheet[imaging][${index}][image_type]`}
              defaultValue={row.image_type || ""}
              placeholder="Image type"
              className="app-input mt-0"
            />
            <input
              type="text"
              name={`face_sheet[imaging][${index}][results]`}
              defaultValue={row.results || ""}
              placeholder="Results"
              className="app-input mt-0"
            />
          </div>
        ))}
        <SectionFormActions onClose={onClose} />
      </form>
    );
  }

  if (openSection === "diagnoses") {
    title = "ICD-10 selection";
    content = (
      <form {...formProps}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="_method" value="patch" />
        <div className="grid gap-2.5 sm:grid-cols-2">
          {(payload.diagnoses || []).map((diagnosis, index) => (
            <label
              key={diagnosis.code}
              className="app-check-card"
            >
              <input type="hidden" name={`face_sheet[diagnoses][${index}][code]`} value={diagnosis.code} />
              <input
                type="hidden"
                name={`face_sheet[diagnoses][${index}][source][type]`}
                value={diagnosisSourceValue(diagnosis, "type")}
              />
              <input
                type="hidden"
                name={`face_sheet[diagnoses][${index}][source][label]`}
                value={diagnosisSourceValue(diagnosis, "label")}
              />
              <input type="hidden" name={`face_sheet[diagnoses][${index}][selected]`} value="0" />
              <input
                type="checkbox"
                name={`face_sheet[diagnoses][${index}][selected]`}
                value="1"
                defaultChecked={Boolean(diagnosis.selected)}
                className="mt-1 h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span>
                <span className="block font-semibold text-neutral-950 dark:text-white">{diagnosis.code}</span>
                <span className="block text-sm text-neutral-600 dark:text-neutral-300">{diagnosis.label}</span>
              </span>
            </label>
          ))}
        </div>
        <SectionFormActions onClose={onClose} />
      </form>
    );
  }

  if (openSection === "notes") {
    title = "Staff notes";
    content = (
      <form {...formProps}>
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="_method" value="patch" />
        <textarea
          name="face_sheet[staff_notes]"
          defaultValue={payload.staff_notes || ""}
          rows={10}
          className="app-input mt-0 min-h-44"
        />
        <SectionFormActions onClose={onClose} />
      </form>
    );
  }

  return <Modal title={title} onClose={onClose} size={openSection === "labs" ? "wide" : "default"}>{content}</Modal>;
}
